import { ChevronDown, PlusIcon } from "lucide-react";
import { Link } from "react-router";

import { useTranslation } from "@punto/frontend-shared/i18n/i18next";
import { Button } from "@punto/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@punto/ui/components/dropdown-menu";

import { useBusinessAbilities } from "@/modules/abilities";
import { routePaths } from "@/router/route-paths";

/**
 * The single creation affordance in the header, in the one shape that fits the
 * agency looking at it.
 *
 * An agency lives in one of two worlds — vehicle procedures (AUTO) or energy
 * saving certificates (ESC) — and, since Punto started using its own
 * organization, possibly in both. A `<Can>` per world would stack two primary
 * buttons side by side for those agencies, so the decision is taken here with
 * `useBusinessAbilities`, which can see both answers at once:
 *
 * - AUTO only  → "Nuevo trámite", straight to the procedure form.
 * - ESC only   → "Nueva actuación", straight to the certificate form.
 * - both       → one "Nuevo trámite" trigger that asks which world first.
 *
 * There is deliberately no "Nuevo lote" button any more (P-2863): batches are
 * still routable at `/batches/new`, just not advertised in the header.
 */
const NewProcedureButton = () => {
  const { t } = useTranslation();
  const { canManageProcedures, canManageEnergySavingCertificates } =
    useBusinessAbilities();

  if (canManageProcedures && canManageEnergySavingCertificates) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button data-testid="header-new-procedure-menu">
            <PlusIcon className="mr-2 size-4" />
            {t("Header.new_procedure")}
            <ChevronDown className="ml-2 size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link
              to={routePaths.newProcedure}
              data-testid="header-new-procedure-option"
            >
              {t("Header.new_procedure_menu.procedure")}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              to={routePaths.newEnergySavingCertificate}
              data-testid="header-new-energy-saving-certificate-option"
            >
              {t("Header.new_procedure_menu.energy_saving_certificate")}
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (canManageProcedures) {
    return (
      <Button asChild>
        <Link to={routePaths.newProcedure} data-testid="header-new-procedure">
          <PlusIcon className="mr-2 size-4" />
          {t("Header.new_procedure")}
        </Link>
      </Button>
    );
  }

  if (canManageEnergySavingCertificates) {
    return (
      <Button asChild>
        <Link
          to={routePaths.newEnergySavingCertificate}
          data-testid="header-new-energy-saving-certificate"
        >
          {t("Header.new_energy_saving_certificate")}
        </Link>
      </Button>
    );
  }

  return null;
};

export { NewProcedureButton };
